import { type Color, KING, PAWN, TYPE_MASK, colorBit, kingBlastThreats, other, rulesFor, explode } from '@chessbut/engine';
import type { Decoration, VariantUi } from './ui.ts';

const BOOM = '/art/boom.svg';

/** Enemy pawns next to `owner`'s king that `owner` could capture: a blast would end the game. */
function kingKillers(board: Int8Array, royals: [number, number], owner: Color): number[] {
  const rules = rulesFor(explode);
  const victim = other(owner);
  const k = royals[victim];
  if (k < 0) return [];
  const out: number[] = [];
  const pawn = PAWN | colorBit(victim);
  for (let sq = 0; sq < 64; sq++) {
    if (board[sq] !== pawn) continue;
    if (Math.max(Math.abs((sq & 7) - (k & 7)), Math.abs((sq >> 3) - (k >> 3))) !== 1) continue;
    if (rules.isAttacked(board, sq, owner)) out.push(sq);
  }
  return out;
}

export const explodeUi: VariantUi = {
  heroImage: BOOM,
  peek: [{ image: '/art/bK.svg', threat: true }, {}, { image: '/art/bN.svg', threat: true }, {}, { image: '/art/bP.svg' }, {}, { image: '/art/wN.svg' }, {}, {}],

  decorations(pos) {
    const out: Decoration[] = [];
    for (const c of [0, 1] as Color[]) {
      for (const sq of kingBlastThreats(pos, c)) out.push({ kind: 'mark', sq, tone: 'danger', hint: true });
    }
    return out;
  },

  twist(pos, you) {
    const me: Color = you ?? pos.turn;
    if (kingBlastThreats(pos, me).length) {
      return { text: you === null ? 'A king is next to a pawn that can be blown up!' : 'Your king is next to a pawn they can take. Kaboom!', alert: true };
    }
    if (pos.turn === me && kingKillers(pos.board, pos.royals, me).length) {
      return { text: 'Take the pawn next to their king to blow it up!' };
    }
    return { text: 'Capture a pawn and everything around it explodes' };
  },

  threatText: (you) => (you ? 'Your king is next to a pawn they can take. Move it!' : 'A king is next to a pawn that can explode!'),

  eventSound(e) {
    return e.t === 'fx' && e.kind === 'boom' ? 'boom' : null;
  },

  moveBadge(rec) {
    if (!rec.events.some((e) => e.t === 'fx' && e.kind === 'boom')) return null;
    const killed = rec.events.filter((e) => e.t === 'remove' && e.cause === 'explosion');
    const king = killed.find((e) => e.t === 'remove' && (e.piece & TYPE_MASK) === KING);
    return { icon: BOOM, victim: king && king.t === 'remove' ? king.piece : undefined, title: killed.length ? `Kaboom: ${killed.length} destroyed` : 'Kaboom' };
  },
};
